import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/**
 * M10 L10.03 — Quality Rework List.
 *
 * Lists reviews that have been sent back for rework
 * (`GET /v1/quality/reviews?status=REWORK_REQUESTED`). Reviewer opens a
 * review to inspect the rework cycle and re-score once the vendor resubmits.
 */
interface ReviewRow {
  readonly reviewId: string;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly reviewStatus: string;
  readonly slaDeadlineAt: string;
  readonly cycleNo: number;
  /** A completion review has no number of its own; it is known by its job. */
  readonly jobNumber: string | null;
}

interface DashboardResponse {
  readonly rows: readonly ReviewRow[];
  readonly total: number;
}

export function QualityReworkPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiRequest<DashboardResponse>(
        '/v1/quality/reviews?status=REWORK_REQUESTED&limit=100', { token },
      );
      setData({ rows: Array.isArray(r.rows) ? r.rows : [], total: r.total ?? 0 });
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="quality-rework">
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('quality.rework.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('quality.rework.intro')}
      </p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!data ? (
        <p>{t('common.loading')}</p>
      ) : data.rows.length === 0 ? (
        <p data-testid="rework-empty" style={{ color: 'var(--color-text-muted)' }}>{t('quality.rework.empty')}</p>
      ) : (
        <DataTable<ReviewRow>
          testId="rework-table"
          ariaLabel="rework-table"
          rowKey={(r) => r.reviewId}
          rowTestId={(r) => `rework-row-${r.reviewId}`}
          columns={[
            { key: 'job', header: t('quality.col.job'), render: (r) => <Link to={`/quality/review/${r.reviewId}`}>{r.jobNumber ?? '—'}</Link> },
            { key: 'vendor', header: t('quality.col.vendor'), render: (r) => personLabel(r.vendorName, r.vendorCode) ?? '—' },
            { key: 'cycle', header: t('quality.col.cycle'), render: (r) => r.cycleNo },
            { key: 'sla', header: t('quality.col.sla'), render: (r) => at(r.slaDeadlineAt) },
          ]}
          rows={data.rows}
        />
      )}
    </main>
  );
}
